import { Bell, Heart, MapPin, PackageCheck, ShieldCheck, User, Wrench } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink, useParams } from "react-router-dom";
import { ProductCard } from "../../components/commerce/ProductCard";
import { StatusPill } from "../../components/ui/StatusPill";
import { accountNav } from "../../data/navigation";
import { products } from "../../data/products";

type AccountView =
  | "dashboard"
  | "orders"
  | "order-detail"
  | "addresses"
  | "wishlist"
  | "warranty"
  | "ask"
  | "notifications"
  | "profile"
  | "security";

type AccountPageProps = {
  view: AccountView;
};

const viewTitles: Record<AccountView, { title: string; summary: string }> = {
  dashboard: {
    title: "Tài khoản",
    summary: "KPI mua hàng, đơn gần đây, Hỏi là có, wishlist và trạng thái bảo hành."
  },
  orders: {
    title: "Đơn hàng",
    summary: "Lịch sử đơn hàng, trạng thái xử lý và tracking."
  },
  "order-detail": {
    title: "Chi tiết đơn",
    summary: "Timeline, sản phẩm, địa chỉ, thanh toán và hỗ trợ sau bán."
  },
  addresses: {
    title: "Địa chỉ",
    summary: "Sổ địa chỉ, thêm/sửa địa chỉ nhận hàng."
  },
  wishlist: {
    title: "Yêu thích",
    summary: "Danh sách phụ tùng đã lưu và thao tác thêm vào giỏ."
  },
  warranty: {
    title: "Bảo hành",
    summary: "Tạo yêu cầu bảo hành/đổi trả và theo dõi xử lý."
  },
  ask: {
    title: "Yêu cầu Hỏi là có",
    summary: "Lịch sử sourcing phụ tùng, báo giá và ghi chú kỹ thuật."
  },
  notifications: {
    title: "Thông báo",
    summary: "Cập nhật đơn hàng, bảo hành, Hỏi là có và ưu đãi."
  },
  profile: {
    title: "Hồ sơ",
    summary: "Thông tin cá nhân, email, phone, loại tài khoản."
  },
  security: {
    title: "Bảo mật",
    summary: "Mật khẩu, email OTP, phiên đăng nhập và cảnh báo."
  }
};

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : undefined);

export function AccountPage({ view }: AccountPageProps) {
  const { id } = useParams();
  const copy = viewTitles[view];

  return (
    <main className="page-canvas">
      <section className="container account-layout">
        <aside className="account-side">
          <div className="account-user">
            <User aria-hidden="true" />
            <strong>Nguyễn Văn Hòa</strong>
            <span>Khách hàng B2C</span>
          </div>
          <nav aria-label="Tài khoản">
            {accountNav.map((item) => (
              <NavLink key={item.href} to={item.href} className={navClass} end={item.href === "/account"}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <section className="account-main">
          <div className="page-title">
            <h1>{copy.title}</h1>
            <p>{copy.summary}</p>
          </div>
          {view === "wishlist" ? (
            <div className="product-grid account-products">
              {products.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          ) : (
            <AccountContent view={view} orderId={id} />
          )}
        </section>
      </section>
    </main>
  );
}

function AccountContent({ view, orderId }: { view: AccountView; orderId?: string }) {
  if (view === "dashboard") {
    return (
      <div className="account-grid">
        <AccountTile icon={<PackageCheck />} label="Đơn gần đây" value="4 đơn" />
        <AccountTile icon={<Heart />} label="Yêu thích" value="12 SKU" />
        <AccountTile icon={<Wrench />} label="Hỏi là có" value="2 yêu cầu" />
        <AccountTile icon={<ShieldCheck />} label="Bảo hành" value="1 hồ sơ" />
      </div>
    );
  }

  if (view === "order-detail") {
    return (
      <div className="account-panel">
        <h2>Đơn {orderId ?? "STUT-260829-001"}</h2>
        <div className="timeline compact">
          <span className="active">Đã tạo</span>
          <span className="active">Đang xác nhận</span>
          <span>Đóng gói</span>
          <span>Bàn giao</span>
        </div>
        <div className="order-lines">
          {products.slice(0, 3).map((product) => (
            <div key={product.id}>
              <strong>{product.name}</strong>
              <StatusPill>Đang xử lý</StatusPill>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const iconMap: Partial<Record<AccountView, ReactNode>> = {
    dashboard: <User />,
    orders: <PackageCheck />,
    "order-detail": <PackageCheck />,
    addresses: <MapPin />,
    wishlist: <Heart />,
    warranty: <ShieldCheck />,
    ask: <Wrench />,
    notifications: <Bell />,
    profile: <User />,
    security: <ShieldCheck />
  };
  const icon = iconMap[view] ?? <User />;

  return (
    <div className="account-panel">
      <div className="panel-title">
        {icon}
        <h2>{viewTitles[view].title}</h2>
      </div>
      <div className="order-lines">
        {["Đang xử lý", "Hoàn tất", "Cần bổ sung"].map((status, index) => (
          <div key={status}>
            <strong>{viewTitles[view].title} #{index + 1}</strong>
            <StatusPill>{status}</StatusPill>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountTile({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="account-tile">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
